<template>
  <b-modal
    :id="modalId"
    :title="$t('unit.assign_task')"
    size="md"
    no-close-on-esc
    no-close-on-backdrop
    @hidden="onModalHidden"
  >
    <b-form>
      <b-form-group
        :label="$tc('users.title', 1)"
        label-for="user-select"
        class="text-capitalize"
      >
        <v-select
          id="user-select"
          v-model="form.user"
          :options="filteredUserOptions"
          :placeholder="$t('messages.info.select') + ' ' + $tc('users.title', 1)"
          :clearable="true"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          @search="searchUsers"
        />
        <small v-if="errors.user" class="text-danger">{{ errors.user }}</small>
      </b-form-group>

      <b-form-group
        :label="$t('unit.task_type')"
        label-for="type-select"
        class="text-capitalize"
      >
        <v-select
          id="type-select"
          v-model="form.type"
          :options="taskTypeOptions"
          :reduce="option => option"
          :disabled="isTypeSelectDisabled"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
        />
        <small v-if="errors.type" class="text-danger">{{ errors.type }}</small>
      </b-form-group>

      <template v-if="showReferenceAndTemplateFields">
        <b-form-group
          :label="$t('unit.reference_number')"
          label-for="reference-display"
          class="text-capitalize"
        >
          <b-form-input
            id="reference-display"
            :value="formAnswerReference"
            disabled
          />
        </b-form-group>
        <b-form-group
          :label="$t('unit.template_label')"
          label-for="template-display"
          class="text-capitalize"
        >
          <b-form-input
            id="template-display"
            :value="displayTemplateValue"
            disabled
          />
        </b-form-group>
      </template>
      <b-form-group
        v-else
        :label="$t(entityLabelKey)"
        label-for="entity-select"
        class="text-capitalize"
      >
        <v-select
          id="entity-select"
          v-model="form.unit"
          :options="entityOptions"
          :placeholder="$t('messages.info.select') + ' ' + $t(entityLabelKey)"
          :clearable="false"
          :disabled="isEntitySelectDisabled"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
        />
        <small v-if="errors.unit" class="text-danger">{{ errors.unit }}</small>
      </b-form-group>

      <b-form-group
        v-if="form.type && form.type.value === TASK_TYPE_FORM_CREATED_WITH_RELATION"
        :label="$t('unit.task_type_form')"
        label-for="type-select-created"
        class="text-capitalize"
      >
        <v-select
          id="type-select-created"
          v-model="form.id_template_to_create"
          :options="templatesOptions"
          :reduce="option => option"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
        />
        <small v-if="errors.type_to_create" class="text-danger">{{ errors.type_to_create }}</small>
      </b-form-group>

      <b-form-group
        :label="$t('unit.disable_comment')"
        label-for="comment-input"
        class="text-capitalize"
      >
        <b-form-textarea
          id="comment-input"
          v-model="form.comment"
          :placeholder="$t('unit.disable_comment')"
          rows="4"
          max-rows="8"
        />
        <small v-if="errors.comment" class="text-danger">{{ errors.comment }}</small>
      </b-form-group>
    </b-form>

    <template #modal-footer>
      <b-button
        variant="secondary"
        @click="$bvModal.hide(modalId)"
        :disabled="loading"
        class="text-capitalize"
      >
        {{ $t("actions.cancel") }}
      </b-button>
      <b-button
        variant="primary"
        @click="submitForm"
        :disabled="loading || !isFormValid"
        class="text-capitalize"
      >
        <b-spinner v-if="loading" small class="mr-50" />
        <feather-icon v-else icon="CheckIcon" class="mr-50" />
        {{ $t("unit.assign_task") }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { ref, computed, watch } from "@vue/composition-api";
import vSelect from "vue-select";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { getUserData } from "@/auth/utils";
import { debounce } from "lodash";
import axios from "@/libs/axios";

const TASK_TYPE_UNIT = "unit";
const TASK_TYPE_FORM = "form";
const TASK_TYPE_ANSWERED_FORM = "answered-form";
const TASK_TYPE_FORM_CREATED_WITH_RELATION = "form-created-with-relation";

export default {
  components: {
    vSelect,
  },
  props: {
    modalId: {
      type: String,
      default: "assign-task-modal",
    },
    taskTypeContext: {
      type: String,
      default: "unit",
      validator: (value) => ["unit", "form", "answered-form"].includes(value),
    },
    allowBothTypes: {
      type: Boolean,
      default: false,
    },
    initialTaskType: {
      type: String,
      default: null,
      validator: (value) => value == null || value === "unit" || value === "form" || value === "answered-form",
    },
    unitId: {
      type: Number,
      default: null,
    },
    unitOptions: {
      type: Array,
      default: () => [],
    },
    formAnswerId: {
      type: Number,
      default: null,
    },
    formAnswerOptions: {
      type: Array,
      default: () => [],
    },
    formAnswerReference: {
      type: String,
      default: null,
    },
    formAnswerTemplateId: {
      type: [Number, String],
      default: null,
    },
    formAnswerTemplateName: {
      type: String,
      default: null,
    },
    userOptions: {
      type: Array,
      default: () => [],
    },
    fetchUsers: {
      type: Function,
    },
    formAnswerTemplate: {
      type: Object,
      default: () => {},
    },
  },
  setup(props, context) {
    const { emit } = context;
    const toast = useToast();
    const loading = ref(false);
    const currentUser = getUserData();

    const translate = (key, params = {}) => {
      return context.root.$t(key, params);
    };

    const isOpeningFromRow = computed(
      () => props.formAnswerId != null || props.formAnswerReference != null
    );

    const templatesOptions = computed(
      () => props?.formAnswerTemplate?.related_templates.map(item => ({
        value: item.id,
        label: item.name
      })) || []
    );

    const hasFormContext = computed(
      () =>
        isOpeningFromRow.value ||
        props.allowBothTypes ||
        props.taskTypeContext === TASK_TYPE_FORM ||
        props.taskTypeContext === TASK_TYPE_ANSWERED_FORM
    );

    const isOperationalSecurityFormAnswersRoute = computed(
      () => ["/operational_security/form-answers", "/operational_security/form-answers-bau"].includes(context.root.$route?.path)
    );

    const taskTypeOptions = computed(() => {
      if (isOpeningFromRow.value) {
        return [
          { value: TASK_TYPE_ANSWERED_FORM, label: translate("unit.task_type_completar_form") },
          { value: TASK_TYPE_FORM_CREATED_WITH_RELATION, label: translate("unit.task_type_create_form_with_relation") },
        ];
      }
      if (hasFormContext.value) {
        if (isOperationalSecurityFormAnswersRoute.value) {
          return [
            { value: TASK_TYPE_FORM, label: translate("unit.task_type_form") },
          ];
        }
        return [
          { value: TASK_TYPE_UNIT, label: translate("unit.task_type_inmuebles") },
          { value: TASK_TYPE_FORM, label: translate("unit.task_type_form") },
        ];
      }
      return [
        { value: TASK_TYPE_UNIT, label: translate("unit.task_type_inmuebles") },
      ];
    });

    const defaultType = computed(() => {
      if (!taskTypeOptions.value.length) return null;
      if (isOpeningFromRow.value) {
        return taskTypeOptions.value[0];
      }
      if (props.taskTypeContext === TASK_TYPE_UNIT || props.unitId != null) {
        return taskTypeOptions.value.find((opt) => opt.value === TASK_TYPE_UNIT) || taskTypeOptions.value[0] || null;
      }
      return taskTypeOptions.value.find((opt) => opt.value === TASK_TYPE_FORM) || taskTypeOptions.value[0] || null;
    });

    const currentType = computed(
      () =>
        form.value.type?.value ??
        (isOpeningFromRow.value
          ? TASK_TYPE_ANSWERED_FORM
          : props.taskTypeContext === TASK_TYPE_UNIT || props.unitId != null
          ? TASK_TYPE_UNIT
          : hasFormContext.value
          ? TASK_TYPE_FORM
          : TASK_TYPE_UNIT)
    );

    const entityOptions = computed(() => {
      if (currentType.value === TASK_TYPE_ANSWERED_FORM && props.formAnswerId != null && (props.formAnswerReference != null || props.formAnswerTemplateId != null)) {
        const reference = props.formAnswerReference || "";
        const templateLabel = props.formAnswerTemplateName != null ? props.formAnswerTemplateName : (props.formAnswerTemplateId != null ? String(props.formAnswerTemplateId) : "");
        const label = reference && templateLabel
          ? `Reference: ${reference} | Template: ${templateLabel}`
          : reference || templateLabel || String(props.formAnswerId);
        return [{ value: props.formAnswerId, label }];
      }
      if (currentType.value === TASK_TYPE_FORM || currentType.value === TASK_TYPE_ANSWERED_FORM) {
        return props.formAnswerOptions || [];
      }
      return props.unitOptions || [];
    });

    const entityId = computed(() => {
      if (currentType.value === TASK_TYPE_FORM || currentType.value === TASK_TYPE_ANSWERED_FORM) {
        return props.formAnswerId;
      }
      return props.unitId;
    });

    const entityLabelKey = computed(() => {
      if (currentType.value === TASK_TYPE_FORM || currentType.value === TASK_TYPE_ANSWERED_FORM || currentType.value === TASK_TYPE_FORM_CREATED_WITH_RELATION) {
        return "unit.task_type_form";
      }
      return "unit.task_type_inmuebles";
    });

    const isOpenedFromUnit = computed(() => props.unitId != null);

    const isTypeSelectDisabled = computed(() => isOpenedFromUnit.value);

    const isEntitySelectDisabled = computed(
      () => currentType.value === TASK_TYPE_ANSWERED_FORM || isOpenedFromUnit.value
    );

    const showReferenceAndTemplateFields = computed(
      () =>
        (currentType.value === TASK_TYPE_ANSWERED_FORM || currentType.value === TASK_TYPE_FORM_CREATED_WITH_RELATION) &&
        (props.formAnswerReference != null || props.formAnswerTemplateName != null || props.formAnswerTemplateId != null)
    );

    const displayTemplateValue = computed(
      () => props.formAnswerTemplateName ?? (props.formAnswerTemplateId != null ? String(props.formAnswerTemplateId) : "")
    );

    const form = ref({
      user: null,
      type: null,
      unit: null,
      comment: "",
    });

    const errors = ref({
      user: null,
      type: null,
      unit: null,
      comment: null,
    });

    const filteredUserOptions = computed(() => {
      /*if (!currentUser || !currentUser.id) {
        return props.userOptions;
      }
      return props.userOptions.filter((user) => user.value !== currentUser.id);*/
      return props.userOptions;
    });

    watch(
      () => [entityId.value, entityOptions.value, () => form.value.type, isOpeningFromRow.value, defaultType.value],
      () => {
        const shouldSyncType =
          !form.value.type ||
          isOpeningFromRow.value ||
          (!isOpeningFromRow.value && form.value.type?.value === TASK_TYPE_ANSWERED_FORM) ||
          (props.unitId != null && form.value.type?.value !== TASK_TYPE_UNIT);
        if(form.value?.type?.value === TASK_TYPE_FORM_CREATED_WITH_RELATION) return;
        if (shouldSyncType && defaultType.value) {
          form.value.type = defaultType.value;
        }
        if (!entityOptions.value.length) {
          form.value.unit = null;
          return;
        }
        const selected = entityId.value
          ? entityOptions.value.find((opt) => opt.value === entityId.value) || null
          : null;
        form.value.unit = selected;
      },
      { immediate: true }
    );

    const isFormValid = computed(() => {
      return (
        form.value.user &&
        form.value.type &&
        form.value.unit &&
        form.value.comment &&
        form.value.comment.trim() !== ""
      );
    });

    const clearErrors = () => {
      errors.value = {
        user: null,
        type: null,
        unit: null,
        comment: null,
      };
    };

    const validateForm = () => {
      clearErrors();
      let isValid = true;

      if (!form.value.user) {
        errors.value.user = translate("messages.validation.required", {
          field: translate("users.title"),
        });
        isValid = false;
      }

      if (!form.value.type) {
        errors.value.type = translate("messages.validation.required", {
          field: translate("unit.task_type"),
        });
        isValid = false;
      }

      if (!form.value.unit) {
        errors.value.unit = translate("messages.validation.required", {
          field: translate(entityLabelKey.value),
        });
        isValid = false;
      }

      if (!form.value.comment || form.value.comment.trim() === "") {
        errors.value.comment = translate("messages.validation.required", {
          field: translate("comment.title"),
        });
        isValid = false;
      }

      if(form.value.type?.value === TASK_TYPE_FORM_CREATED_WITH_RELATION && !form.value.id_template_to_create) {
        errors.value.type_to_create = translate("messages.validation.required", {
          field: translate("unit.task_type_form"),
        });
        isValid = false;
      }

      return isValid;
    };

    const resetForm = () => {
      const typeValue = defaultType.value?.value;
      const isFormType = typeValue === TASK_TYPE_FORM || typeValue === TASK_TYPE_ANSWERED_FORM;
      const id = isFormType ? props.formAnswerId : props.unitId;
      const options = isFormType ? (props.formAnswerOptions || []) : (props.unitOptions || []);
      const selectedEntity = id && options.length ? options.find((opt) => opt.value === id) || null : null;
      form.value = {
        user: null,
        type: defaultType.value,
        unit: selectedEntity,
        comment: "",
      };
      clearErrors();
    };

    const onModalHidden = () => {
      resetForm();
      emit("hidden");
    };

    const buildPayload = () => {
      const typeValue = form.value.type?.value ?? props.taskTypeContext ?? TASK_TYPE_UNIT;
      const payload = {
        id_user: form.value.user.value,
        comment: form.value.comment.trim(),
        type: typeValue === TASK_TYPE_ANSWERED_FORM ? TASK_TYPE_ANSWERED_FORM : typeValue,
      };
      if (typeValue === TASK_TYPE_FORM || typeValue === TASK_TYPE_ANSWERED_FORM || typeValue === TASK_TYPE_FORM_CREATED_WITH_RELATION) {
        payload.id_form = form.value.unit?.value ?? props.formAnswerId;
      } else {
        payload.id_unit = form.value.unit?.value ?? props.unitId;
      }
      if(form.value.type?.value === TASK_TYPE_FORM_CREATED_WITH_RELATION) {
        payload.id_template_to_create = form.value.id_template_to_create?.value;
      }
      return payload;
    };

    const submitForm = async () => {
      if (!validateForm()) {
        return;
      }

      loading.value = true;

      try {
        const payload = buildPayload();
        const response = await store.dispatch("unit/assignTask", payload);

        toast({
          component: ToastificationContent,
          props: {
            title:
              response?.message || translate("messages.success.task_assigned"),
            icon: "CheckIcon",
            variant: "success",
          },
        });

        resetForm();
        emit("task-assigned");
        emit("refetch-data");

        setTimeout(() => {
          context.root.$bvModal.hide(props.modalId);
        }, 500);
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title:
              error.response?.data?.message ||
              translate("messages.error.server_error"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        loading.value = false;
      }
    };

    const searchUsers = debounce(async (searchQuery, setLoading) => {
      setLoading(true);
      await props.fetchUsers(searchQuery);
      setLoading(false);
    }, 350);

    return {
      form,
      errors,
      loading,
      taskTypeOptions,
      entityOptions,
      entityLabelKey,
      filteredUserOptions,
      isFormValid,
      isEntitySelectDisabled,
      showReferenceAndTemplateFields,
      templatesOptions,
      displayTemplateValue,
      isTypeSelectDisabled,
      TASK_TYPE_FORM_CREATED_WITH_RELATION,
      resetForm,
      onModalHidden,
      submitForm,
      searchUsers
    };
  },
};
</script>

<style scoped>
.text-capitalize {
  text-transform: capitalize;
}
</style>

